import { format, parseISO } from "date-fns";
import { CheckCircle2, Eye, MousePointerClick, AlertTriangle } from "lucide-react";
import { LogExport } from "@/components/admin/LogExport";

const _fmt = (iso) => (iso ? format(parseISO(iso), "d MMM · HH:mm") : "—");

export function EmailsTab({ emails, emailsBusy, loadEmails }) {
  // Roll-up counters shown as a compact strip at the top so admins can see the
  // whole club's read-through rate at a glance (populated by the Resend
  // webhook — the fields stay 0 for emails sent before the webhook went live).
  const rollup = (emails || []).reduce((acc, e) => {
    acc.total += 1;
    if (e.delivered_at) acc.delivered += 1;
    if (e.open_count) acc.opened += 1;
    if (e.click_count) acc.clicked += 1;
    if (e.status === "bounced" || e.status === "failed") acc.bounced += 1;
    return acc;
  }, { total: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0 });
  const pct = (n) => (rollup.total ? Math.round((n / rollup.total) * 100) : 0);

  return (
    <div className="space-y-4">
      <div className="card-tactical rounded-md p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="font-display font-bold text-2xl tracking-tight">Email history</div>
            <p className="text-sm opacity-60 mt-1">Every outgoing email — booking confirmations, referee assignments and system messages. Delivered / Opened / Clicked columns are populated live by the Resend webhook.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <LogExport kind="email" />
            <button onClick={loadEmails} data-testid="emails-refresh" className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded transition-colors">{emailsBusy ? "Loading…" : "Refresh"}</button>
          </div>
        </div>
      </div>

      {rollup.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="emails-rollup">
          <div className="card-tactical rounded-md p-4">
            <div className="text-[10px] tracking-widest uppercase opacity-60">Sent</div>
            <div className="font-display font-black text-2xl mt-1">{rollup.total}</div>
          </div>
          <div className="card-tactical rounded-md p-4 border-[#83B68B]/40 bg-[#83B68B]/[0.04]">
            <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Delivered</div>
            <div className="font-display font-black text-2xl mt-1 text-[#83B68B]">{rollup.delivered} <span className="text-xs opacity-60">· {pct(rollup.delivered)}%</span></div>
          </div>
          <div className="card-tactical rounded-md p-4">
            <div className="text-[10px] tracking-widest uppercase opacity-60">Opened</div>
            <div className="font-display font-black text-2xl mt-1">{rollup.opened} <span className="text-xs opacity-60">· {pct(rollup.opened)}%</span></div>
          </div>
          <div className={`card-tactical rounded-md p-4 ${rollup.bounced ? "border-amber-400/50 bg-amber-500/[0.04]" : ""}`}>
            <div className={`text-[10px] tracking-widest uppercase ${rollup.bounced ? "text-amber-300" : "opacity-60"}`}>Bounced / Failed</div>
            <div className={`font-display font-black text-2xl mt-1 ${rollup.bounced ? "text-amber-300" : ""}`}>{rollup.bounced}</div>
          </div>
        </div>
      )}

      <div className="card-tactical rounded-md overflow-hidden overflow-x-auto">
        {!emails || emails.length === 0 ? (
          <div className="p-8 text-sm opacity-60 text-center">No emails sent yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="emails-table">
            <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">To</th>
                <th className="text-left px-4 py-3">Subject</th>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3" title="Delivered by Resend">Delivered</th>
                <th className="text-left px-4 py-3" title="Recipient opened the email at least once">Opened</th>
                <th className="text-left px-4 py-3" title="Recipient clicked a link">Clicked</th>
              </tr>
            </thead>
            <tbody>
              {emails.map(e => (
                <tr key={e.id} className="border-t border-border" data-testid={`email-row-${e.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap opacity-80">{_fmt(e.at)}</td>
                  <td className="px-4 py-3"><a href={`mailto:${e.to}`} className="hover:text-[#83B68B]">{e.to}</a></td>
                  <td className="px-4 py-3 opacity-90">{e.subject}</td>
                  <td className="px-4 py-3"><span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-muted">{e.kind}</span></td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded ${e.status === "delivered" ? "bg-[#83B68B]/20 text-[#83B68B]" : e.status === "sent" ? "bg-slate-500/20 opacity-80" : e.status === "bounced" || e.status === "failed" ? "bg-red-500/20 text-red-500" : e.status === "complained" ? "bg-amber-500/20 text-amber-300" : "bg-muted opacity-70"}`}>{e.status}</span>
                    {e.error && <div className="text-[10px] opacity-60 mt-1">{e.error}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`email-delivered-${e.id}`}>
                    {e.delivered_at
                      ? <span className="inline-flex items-center gap-1 text-[#83B68B]"><CheckCircle2 className="w-3.5 h-3.5" /> {_fmt(e.delivered_at)}</span>
                      : e.status === "bounced" ? <span className="inline-flex items-center gap-1 text-amber-300"><AlertTriangle className="w-3.5 h-3.5" /> Bounced</span>
                      : <span className="opacity-40">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`email-opened-${e.id}`}>
                    {e.open_count
                      ? <span className="inline-flex items-center gap-1 text-[#83B68B]" title={`${e.open_count} open${e.open_count === 1 ? "" : "s"} · first at ${_fmt(e.opened_at)}`}><Eye className="w-3.5 h-3.5" /> {_fmt(e.opened_at)}{e.open_count > 1 ? ` · ${e.open_count}×` : ""}</span>
                      : <span className="opacity-40">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`email-clicked-${e.id}`}>
                    {e.click_count
                      ? <span className="inline-flex items-center gap-1 text-[#83B68B]" title={`${e.click_count} click${e.click_count === 1 ? "" : "s"} · first at ${_fmt(e.clicked_at)}`}><MousePointerClick className="w-3.5 h-3.5" /> {_fmt(e.clicked_at)}{e.click_count > 1 ? ` · ${e.click_count}×` : ""}</span>
                      : <span className="opacity-40">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
